/**
 * Copy an image blob to the clipboard.
 * The Clipboard API only supports image/png, so JPEG/WebP blobs
 * are converted to PNG via an offscreen canvas before writing.
 */
export async function copyImageToClipboard(blob: Blob): Promise<void> {
  let pngBlob = blob;
  if (blob.type !== 'image/png') {
    pngBlob = await convertToPng(blob);
  }
  await navigator.clipboard.write([
    new ClipboardItem({ 'image/png': pngBlob }),
  ]);
}

/**
 * Fetch an image URL and copy it to the clipboard as PNG.
 */
export async function copyImageUrlToClipboard(url: string): Promise<void> {
  const res = await fetch(url);
  const blob = await res.blob();
  await copyImageToClipboard(blob);
}

function convertToPng(blob: Blob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      canvas.toBlob((result) => {
        if (result) resolve(result);
        else reject(new Error('Canvas toBlob returned null'));
      }, 'image/png');
    };
    img.onerror = () => reject(new Error('Failed to load image for PNG conversion'));
    img.crossOrigin = 'anonymous';
    img.src = URL.createObjectURL(blob);
  });
}
